import type { FocusEvent, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

/**
 * Keyboard entry point for an actionable table row.
 *
 * A clickable `<tr>` cannot be reached by keyboard, and giving the row
 * `role="button"`/`role="link"` to make it focusable strips `role="row"`,
 * costing screen readers the row/column context of the whole table. So the
 * action lives in a real control inside one cell instead: an anchor when the
 * row navigates, a button otherwise. Enter (and Space, on the button) then
 * come from the platform -- no `onKeyDown` to hand-roll.
 */

/**
 * `ring-primary` is near-black in light and near-white in dark, so the ring
 * clears the 3:1 a focus indicator needs against the table surface. `block
 * max-w-full` keeps existing cell layouts (notably `truncate`) intact, and the
 * inherited text-align stops a `<button>`'s UA-centred text from fighting a
 * right- or centre-aligned column.
 */
const ROW_ACTIVATOR_CLASS =
  'block max-w-full scroll-m-1 rounded-sm [text-align:inherit] outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** Highlights the whole row while its activator holds keyboard focus. */
export const ROW_ACTIVATOR_ROW_CLASS = 'has-[[data-row-activator]:focus-visible]:bg-muted';

const NESTED_CONTROL_SELECTOR =
  'a,button,input,select,textarea,label,[role="button"],[role="link"],[role="menuitem"],[role="checkbox"],[role="switch"]';

/**
 * True when a row click originated inside a nested control -- the activator
 * itself, an action button, a copy button. Those handle their own activation,
 * so the row handler must stand down or the action fires twice.
 */
export function shouldIgnoreRowClick(event: MouseEvent<HTMLElement>): boolean {
  const target = event.target as HTMLElement | null;
  return Boolean(target?.closest?.(NESTED_CONTROL_SELECTOR));
}

export interface RowActivatorLink {
  to: string;
  state?: unknown;
}

interface RowActivatorProps {
  /** Renders a real link when the row navigates; omit for dialog/expand rows. */
  link?: RowActivatorLink | undefined;
  /** Accessible name identifying the record, e.g. `Open client Acme Corp`. */
  label?: string | undefined;
  /** Called on activation when there is no `link`. */
  onActivate?: (() => void) | undefined;
  className?: string;
  children: ReactNode;
}

/**
 * Tables live in a horizontally scrolling container, and Chromium does not
 * reliably scroll a newly focused row control back into view -- tabbing out of
 * a right-hand header button leaves the ring off-screen. `nearest` on both
 * axes is a no-op when the control is already visible.
 */
function revealOnFocus(event: FocusEvent<HTMLElement>) {
  event.currentTarget.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
}

export function RowActivator({ link, label, onActivate, className, children }: RowActivatorProps) {
  if (link) {
    return (
      <Link
        to={link.to}
        {...(link.state !== undefined && { state: link.state })}
        data-row-activator
        {...(label && { 'aria-label': label })}
        className={cn(ROW_ACTIVATOR_CLASS, className)}
        onFocus={revealOnFocus}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      data-row-activator
      {...(label && { 'aria-label': label })}
      className={cn(ROW_ACTIVATOR_CLASS, className)}
      onClick={() => onActivate?.()}
      onFocus={revealOnFocus}
    >
      {children}
    </button>
  );
}
